import { Zip, ZipPassThrough } from 'fflate';

// Builds an export ZIP in the browser from a server manifest.
//
// The server used to build the ZIP itself, but that means a CRC32 over every
// byte of every photo, which exceeds the Workers Free CPU limit on large
// batches (Cloudflare cut the download off mid-stream). Instead the server
// returns signed R2 URLs and the browser does the work.

export type ExportFile = { filename: string; url: string; size: number | null };
export type ExportManifest = { zipName: string; files: ExportFile[] };

export type ExportProgress = {
  filesDone: number;
  filesTotal: number;
  /** Bytes downloaded so far. */
  received: number;
  /** Total bytes, or null if the server couldn't report every file's size. */
  total: number | null;
};

type FetchFn = (url: string, init?: RequestInit) => Promise<Response>;

const CONCURRENCY = 3;
const ATTEMPTS = 2; // one retry per photo
const PROGRESS_INTERVAL_MS = 100;

/** Downloads one file, reporting bytes as they arrive. Retries once. */
export async function fetchFileBytes(
  file: ExportFile,
  fetchFn: FetchFn,
  onBytes: (delta: number) => void,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let counted = 0;
    try {
      const res = await fetchFn(file.url, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (!res.body) throw new Error('empty response');
      const reader = res.body.getReader();
      const parts: Uint8Array[] = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value);
        counted += value.length;
        onBytes(value.length);
      }
      const out = new Uint8Array(counted);
      let offset = 0;
      for (const p of parts) { out.set(p, offset); offset += p.length; }
      return out;
    } catch (err) {
      if (counted) onBytes(-counted); // undo progress from the failed attempt
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  const reason = lastError instanceof Error ? lastError.message : String(lastError);
  throw new Error(`Couldn't download ${file.filename} (${reason})`);
}

/**
 * Downloads every file in the manifest (a few at a time) and returns the ZIP.
 * Fails as a whole if any photo can't be downloaded, so a partial ZIP is never
 * produced. Photos are stored, not compressed: JPEGs don't shrink.
 */
export async function buildZip(
  manifest: ExportManifest,
  fetchFn: FetchFn,
  onProgress: (progress: ExportProgress) => void,
): Promise<Blob> {
  const { files } = manifest;
  const total = files.every((f) => f.size != null)
    ? files.reduce((sum, f) => sum + (f.size ?? 0), 0)
    : null;

  let received = 0;
  let filesDone = 0;
  let lastReport = 0;
  const report = (force = false) => {
    const now = Date.now();
    if (!force && now - lastReport < PROGRESS_INTERVAL_MS) return;
    lastReport = now;
    onProgress({ filesDone, filesTotal: files.length, received, total });
  };
  report(true);

  const out: Uint8Array[] = [];
  let zipError: Error | null = null;
  let finish!: () => void;
  const finished = new Promise<void>((resolve) => { finish = resolve; });
  const zip = new Zip((err, data, final) => {
    if (err) { zipError = err; finish(); return; }
    out.push(data);
    if (final) finish();
  });

  // Stop the other downloads as soon as one photo fails for good.
  const abort = new AbortController();
  let next = 0;
  const worker = async () => {
    while (next < files.length && !abort.signal.aborted) {
      const file = files[next++];
      const bytes = await fetchFileBytes(file, fetchFn, (delta) => { received += delta; report(); }, abort.signal);
      const entry = new ZipPassThrough(file.filename);
      zip.add(entry);
      entry.push(bytes, true);
      filesDone++;
      report();
    }
  };

  try {
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));
  } catch (err) {
    abort.abort();
    zip.terminate();
    throw err;
  }

  zip.end();
  await finished;
  if (zipError) throw zipError;
  report(true);
  return new Blob(out as BlobPart[], { type: 'application/zip' });
}
